/**
 * Drawings on a chart — the contract's `Annotation`, resolved for a renderer.
 *
 * Two sources reach the same renderer and only one of them is ever saved, and
 * keeping that straight is the whole design:
 *
 * - **User drawings** are `Pane.annotations`: a trendline someone drew, a box,
 *   a note. They are contract data, shredded into rows by `workspace_store`,
 *   and they persist because the user put them there.
 * - **System drawings** are computed per render — trading-plan entry, stop and
 *   target lines being the first of them. They are **never** written into
 *   `Pane.annotations`, because a derived line that got saved is a number with
 *   two homes: edit the plan and the stored copy still renders, correctly drawn
 *   and wrong. `docs/plans/trading-plan.md` calls that out as the mistake the
 *   obvious implementation makes.
 *
 * So `resolveAnnotations` takes the two separately and marks which is which.
 * The renderer draws both; only one round-trips.
 *
 * ## Colours are tokens until the last moment
 *
 * A style may name a theme token (`negative`) or a literal. `resolveColorRef`
 * turns either into a colour against the active theme, at plan time, so a theme
 * switch repaints a drawing that was made under another one (D13). A stored hex
 * would survive the switch and be the one thing on the chart still wearing the
 * old palette.
 *
 * ## What a plan deliberately does not decide
 *
 * Where the annotation sits in z-order, whether its grips are showing, and how a
 * drag is captured. Those are the surface's, and putting them here would make
 * this file need a mouse.
 */

import { resolveColorRef, type Annotation, type Theme } from "@qufolio/model";

/** The contract's annotation kinds, as the renderer branches on them. */
export type AnnotationKind = Annotation["type"];

/**
 * Whether a drawing is the user's or the system's.
 *
 * Not cosmetic: `derived` annotations are rebuilt from their source every
 * render and must never be collected back into the contract tree. A renderer
 * that lost this distinction would save the trading plan's own lines the next
 * time anything wrote the workspace.
 */
export type AnnotationOrigin = "user" | "derived";

export interface AnnotationStylePlan {
  stroke?: string;
  fill?: string;
  opacity?: number;
  thickness: number;
  dash?: number[];
  fontSize?: number;
  fontColor?: string;
}

export interface AnnotationPlan {
  id: string;
  kind: AnnotationKind;
  origin: AnnotationOrigin;
  /** X in the axis' own units. Absent for a horizontal line, which spans. */
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  text?: string;
  style: AnnotationStylePlan;
  /** Fibonacci or other multi-level values, already coloured. */
  levels: { value: number; color?: string; label?: string }[];
  /**
   * A freehand stroke's path, in the same space as `x1`/`y1`.
   *
   * Separate from the corner pair rather than folded into it: a stroke is not
   * two corners, and packing its bounding box into `x1..y2` would render every
   * freehand drawing as the rectangle it happens to fit inside.
   */
  points: { x: number; y: number }[];
  /**
   * Whether the surface should offer grips.
   *
   * Always false for `derived`: a drag would edit a line whose source is
   * elsewhere, so the edit would be undone by the next render. Better to not
   * offer the handle than to have it snap back.
   */
  editable: boolean;
  /** {@link DerivedAnnotationExtras} — carried for a derived drawing only. */
  exactX?: boolean;
  textAnchor?: "right";
  axisLabel?: string;
  hover?: readonly string[];
}

/**
 * What a **system** drawing may say that a user's cannot.
 *
 * Bar Intelligence (`docs/plans/bar-intelligence.md`) needs three things the
 * contract's `Annotation` has no field for, and none of them belongs in the
 * contract: a derived drawing is rebuilt every render and never saved, so a
 * field for it there would be one every persistence layer has to carry for
 * nothing.
 *
 * - `textAnchor: "right"` — a text whose **right** edge sits at `x1`, bottom on
 *   `y1`: a label ending just left of the current bar, above its line.
 * - `axisLabel` — a tag on the price axis reading `<axisLabel> <price>`, for a
 *   line whose id would otherwise get none (ids with `|` are skipped).
 * - `hover` — lines a popup shows while the pointer is on the drawing.
 */
export interface DerivedAnnotationExtras {
  textAnchor?: "right";
  axisLabel?: string;
  hover?: readonly string[];
  /**
   * Place `x1`/`x2` at exactly these times — no snapping to a bar, no clamp.
   * For a drawing that covers bars edge to edge and may lie wholly past the
   * last bar (Seasonality hits' blocks), where the nearest-bar rule would pull
   * every future block back onto the last bar.
   */
  exactX?: boolean;
}

export type DerivedAnnotation = Annotation & DerivedAnnotationExtras;

/** One device pixel of ink, which is what a drawn line is unless it says otherwise. */
export const DEFAULT_ANNOTATION_THICKNESS = 1;

/**
 * A contract annotation, resolved against the theme.
 *
 * `origin` is passed rather than inferred — nothing on an `Annotation` says
 * where it came from, and a guess ("has an id, must be the user's") is exactly
 * the kind of inference that would eventually save a derived line.
 */
export function resolveAnnotation(
  annotation: Annotation,
  origin: AnnotationOrigin,
  theme?: Theme,
): AnnotationPlan {
  const style = annotation.style ?? {};
  return {
    id: annotation.id,
    kind: annotation.type,
    origin,
    x1: annotation.x1,
    y1: annotation.y1,
    x2: annotation.x2,
    y2: annotation.y2,
    text: annotation.text,
    style: {
      stroke: resolveColorRef(style.stroke, theme),
      fill: resolveColorRef(style.fill, theme),
      opacity: style.opacity,
      thickness: style.thickness ?? DEFAULT_ANNOTATION_THICKNESS,
      dash: style.dash,
      fontSize: style.fontSize,
      fontColor: resolveColorRef(style.fontColor, theme),
    },
    levels: (annotation.levels ?? []).map((level) => ({
      value: level.value ?? 0,
      color: resolveColorRef(level.color, theme),
      label: level.label,
    })),
    points: (annotation.points ?? []).map((point) => ({ x: point.x, y: point.y })),
    editable: origin === "user",
  };
}

/**
 * Every drawing a pane shows, user first and system on top.
 *
 * Order is z-order, and system last is deliberate: a trading plan's stop is the
 * line you are actually watching, and a trendline drawn across it should not
 * hide it. A user who wants their own drawing on top can move the plan's levels
 * out of the way, which is a real action; nobody can raise a derived line.
 */
export function resolveAnnotations(
  user: readonly Annotation[] | undefined,
  derived: readonly DerivedAnnotation[] | undefined,
  theme?: Theme,
  editable?: ReadonlySet<string>,
): AnnotationPlan[] {
  return [
    ...(user ?? []).map((a) => resolveAnnotation(a, "user", theme)),
    ...(derived ?? []).map((a) => {
      const plan: AnnotationPlan = { ...resolveAnnotation(a, "derived", theme) };
      if (a.textAnchor) plan.textAnchor = a.textAnchor;
      if (a.axisLabel) plan.axisLabel = a.axisLabel;
      if (a.hover?.length) plan.hover = a.hover;
      if (a.exactX) plan.exactX = true;
      // **The one way a derived annotation earns a grip.**
      //
      // `origin: "derived"` means false by default, and that default is right:
      // a drag would edit something whose source is elsewhere and the next
      // render would undo it. The exception is a line whose source *can* be
      // written — a trading plan's stop, which has `amendPlan` behind it — so
      // the caller names those ids and takes on reporting the drag.
      //
      // A set of ids rather than a flag on the annotation, because editability
      // is a fact about what the host can write, not about the drawing. The
      // contract has no `editable` field and should not grow one for this.
      return editable?.has(plan.id) ? { ...plan, editable: true } : plan;
    }),
  ];
}

/**
 * The user's drawings only, for a caller about to write the workspace.
 *
 * The guard that makes the two sources safe to render together. Anything
 * collecting annotations back out of a plan has to go through this, or the
 * system's own lines are saved and become a second copy of a number the plan
 * already owns.
 */
export function persistable(plans: readonly AnnotationPlan[]): AnnotationPlan[] {
  return plans.filter((plan) => plan.origin === "user");
}

/**
 * How a draggable level looks while the pointer is on it.
 *
 * **The same idiom the pane dividers use** — `--qf-border` at rest, `--qf-accent`
 * and a pixel thicker while hovered — because a plan level is the same kind of
 * thing: a line you can grab. Matching it means the gesture is learned once.
 *
 * A cursor was the obvious answer and does not work here: SciChart runs its own
 * cursor manager that writes `style.cursor` on the chart root on every mouse
 * move, so anything set from outside is overwritten within a frame.
 *
 * **The resting stroke is passed in, never re-derived.** It is the level's own
 * theme-resolved colour — red for a stop, green for a target — and computing it
 * again here would be a second place for a colour to come from, which is the
 * whole thing D13 exists to prevent.
 *
 * **`accent` is optional, and a user's own drawing passes none.** The swap suits
 * a plan level, whose colour was chosen by the plan; it does not suit a drawing
 * whose colour the user is choosing right now, because the line is highlighted
 * for exactly as long as they are picking and the accent is then the only colour
 * they can see. Absent, it thickens and leaves the stroke alone — which is the
 * same branch a theme that defines no accent already took.
 */
export function hoverStyle(
  stroke: string | undefined,
  thickness: number,
  accent: string | undefined,
  isHovered: boolean,
): { stroke: string | undefined; strokeThickness: number } {
  // A theme need not define every token, and a highlight that painted
  // `undefined` would erase the line rather than light it up. With no accent to
  // take, it still thickens — half the signal beats none of it.
  if (!isHovered) return { stroke, strokeThickness: thickness };
  return { stroke: accent ?? stroke, strokeThickness: thickness + 1 };
}

/**
 * The seven kinds this renderer draws itself, corner-to-corner or as a stroke.
 *
 * Everything else in the contract's enum is a **multi-point** drawing — a
 * pitchfork's three anchors, a channel's three, a Fibonacci wedge's two plus a
 * radius — and is placed and drawn by `scichart-financial-tools` rather than by
 * the code in this package.
 *
 * **Defined by exclusion, and deliberately.** The contract's own description of
 * `Annotation.type` says "everything after `freehand` is a multi-point
 * drawing", so a twenty-sixth kind added to the schema is multi-point without
 * anyone remembering to add it here. A positive list would have to be edited in
 * lockstep with the contract, and the failure when it was not is a drawing that
 * silently resolves as a corner pair — rendered as the rectangle its anchors
 * happen to fit inside.
 */
const CORNER_KINDS: ReadonlySet<AnnotationKind> = new Set<AnnotationKind>([
  "line",
  "hline",
  "text",
  "box",
  "fib",
  "trendline",
  "freehand",
  // One bar: `x1` and no price — a full-height column, `docs/plans/bar-highlights.md`.
  "barHighlight",
]);

/** Whether a kind is anchored by a list of points rather than by corners. */
export function isMultiPoint(kind: AnnotationKind): boolean {
  return !CORNER_KINDS.has(kind);
}

/**
 * Kinds a renderer must place from `points`, before any corner arithmetic.
 *
 * `freehand` plus every multi-point kind. One predicate rather than two checks
 * at the call site, because the two are the same fact — the drawing has no
 * `x1` — and a renderer that handles one and forgets the other produces a bug
 * with no symptom until somebody reaches for that tool. It did: freehand sat
 * below the corner conversion for as long as no palette entry armed it, drew a
 * rubber band, and vanished on release.
 */
export function placedByPoints(kind: AnnotationKind): boolean {
  return kind === "freehand" || isMultiPoint(kind);
}

/**
 * Whether a kind is drawn from two points or one.
 *
 * `hline` spans its pane and carries only a `y1`; `text` sits at a point. The
 * rest are corner-to-corner. The renderer needs this to know which coordinates
 * to demand, and a missing second point on a two-point kind is a drawing that
 * cannot be placed rather than one drawn at zero.
 */
export function needsTwoPoints(kind: AnnotationKind): boolean {
  return kind === "line" || kind === "trendline" || kind === "box" || kind === "fib";
}

/**
 * A `line` with a start and no end — a **ray** to the right edge.
 *
 * The shape a trading plan's levels want: they begin where the plan was written
 * and run to now. Spanning the pane instead (`hline`) draws them across history
 * from years before the idea existed, and with two plans open there is nothing
 * to say which full-width line belongs to which.
 *
 * A convention on the existing `line` rather than a new contract type, because
 * the contract already stores exactly this — an x1 and no x2 — and a `ray` kind
 * would need a schema change, codegen, a .NET type and a store column to say
 * what an absent field already says.
 */
export function isRay(plan: AnnotationPlan): boolean {
  // A `box` too, and for the same reason: a trading plan's risk and reward
  // zones start where the plan does and run to now. A box needs both its
  // prices, so only the far *x* is the renderer's to supply.
  const rayable = plan.kind === "line" || plan.kind === "box";
  return rayable && plan.x1 !== undefined && plan.x2 === undefined;
}

/** Whether this plan carries the coordinates its kind requires. */
export function isPlaceable(plan: AnnotationPlan): boolean {
  // Freehand is the exception to every rule here: it has no corners at all, and
  // a stroke of one point is a dot nobody can see or select.
  if (plan.kind === "freehand") return plan.points.length >= 2;
  // **A multi-point drawing is placed by its points and nothing else.** It has
  // no corner pair to demand, and demanding one would reject every one of them:
  // `y1` is the first anchor's price at best and meaningless at worst — a
  // vertical line has one x and no price at all.
  //
  // Two is the floor rather than each kind's own count, because the count lives
  // in the library (`getPlacementPointCount`) and duplicating twenty-five of
  // them here would be a second source of truth that goes stale the first time
  // the package changes one. A drawing short of what its own class needs draws
  // nothing, which the renderer already handles by returning null.
  if (isMultiPoint(plan.kind)) return plan.points.length >= 2;
  if (plan.kind === "barHighlight") return plan.x1 !== undefined;
  if (plan.y1 === undefined) return false;
  if (plan.kind === "hline") return true;
  if (plan.x1 === undefined) return false;
  // A ray needs only its start; the renderer supplies the far end.
  if (isRay(plan)) return true;
  if (!needsTwoPoints(plan.kind)) return true;
  return plan.x2 !== undefined && plan.y2 !== undefined;
}

/**
 * A `fib`'s levels as prices, in the order they draw.
 *
 * The ratio runs from `y1` to `y2` — the direction of the drag, not of the
 * price — so a retracement drawn down a decline reads 0% at the high, which is
 * how anyone reading one expects it. Sorting the two ends first would put 0% at
 * whichever is numerically smaller and silently flip half of them.
 *
 * Falls back to the default ratios when the annotation carries none, so a `fib`
 * that reaches a renderer without levels draws a retracement rather than
 * nothing at all.
 */
export function fibPrices(
  plan: AnnotationPlan,
  fallback: readonly { value: number; label?: string }[] = [],
): { value: number; price: number; label?: string; color?: string }[] {
  if (plan.y1 === undefined || plan.y2 === undefined) return [];
  const span = plan.y2 - plan.y1;
  const levels = plan.levels.length > 0 ? plan.levels : fallback;
  return levels.map((level) => ({
    value: level.value,
    price: plan.y1! + span * level.value,
    label: level.label,
    color: "color" in level ? (level as { color?: string }).color : undefined,
  }));
}
